import { createModel } from "nyax";
import {
  PackageVersion,
  PackageVersionUpdateData,
} from "src/models/packageVersion";
import { PackageVersionEntityModel } from "src/store/models/entity/packageVersion/entity";
import { createHelperModel } from "src/store/models/entity/_shared";

export const PackageVersionHelperModel = createModel(
  class extends createHelperModel<PackageVersion>({
    setItems: (getContainer, items) =>
      getContainer(PackageVersionEntityModel).actions.setItems.dispatch(items),
    getItems: (getContainer) =>
      getContainer(PackageVersionEntityModel).getters.items,
    getItem: (getContainer, id) =>
      getContainer(PackageVersionEntityModel).state.byId[id],
  }) {
    public selectors() {
      return {
        ...super.selectors(),
        entity: () => this.getContainer(PackageVersionEntityModel),
      };
    }

    public effects() {
      return {
        ...super.effects(),

        readByPackageIds: async (payload: {
          packageIds: string[];
          force?: boolean;
        }) => {
          const { packageIds, force } = payload;
          return await this._readByParentIds({
            parentIds: packageIds,
            getAllAction: (parentId) => {
              return this.dependencies.serviceClient.package.getAllPackageVersions(
                parentId
              );
            },
            getEntityParentId: (entity: PackageVersion) =>
              entity.resourceGroupId,
            force,
          });
        },
        updateCompleted: async (payload: {
          packageId: string;
          packageVersionId: string;
          packageVersionUpdateData: PackageVersionUpdateData;
        }) => {
          const { packageId, packageVersionId, packageVersionUpdateData } =
            payload;

          return this._update({
            id: packageVersionId,
            updateAction: () =>
              this.dependencies.serviceClient.package.updateCompleted(
                packageId,
                packageVersionId,
                packageVersionUpdateData
              ),
          });
        },
        getVersionDetail: async (payload: {
          packageId: string;
          versionId: string;
        }) => {
          const { packageId, versionId } = payload;

          const { timestamp } = await this.actions._rw.beginWrite.dispatch({
            ids: [versionId],
          });

          try {
            const item =
              await this.dependencies.serviceClient.package.getPackageVersionById(
                packageId,
                versionId
              );

            await this.actions._rw.endWrite.dispatch({
              items: [item],
              timestamp,
            });
            return item;
          } catch (error) {
            await this.actions._rw.endWrite.dispatch({
              items: [],
              timestamp,
            });
            throw error;
          }
        },
        delete: async (payload: {
          packageId: string;
          packageVersionId: string;
        }) => {
          const { packageId, packageVersionId } = payload;

          const { timestamp } = await this.actions._rw.beginWrite.dispatch({
            ids: [packageVersionId],
          });

          try {
            await this.dependencies.serviceClient.package.deletePackageVersion(
              packageId,
              packageVersionId
            );

            await this.actions._rw.endWrite.dispatch({
              items: [packageVersionId],
              timestamp,
            });
          } catch (error) {
            await this.actions._rw.endWrite.dispatch({
              items: [],
              timestamp,
            });
            throw error;
          }
        },
      };
    }
  }
);
